import { BooleanInput } from '@angular/cdk/coercion';
import { SelectionModel } from '@angular/cdk/collections';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    Inject,
    Input,
    OnInit,
    Output,
    QueryList,
    ViewChildren,
} from '@angular/core';
import { MatLegacyListOption as MatListOption } from '@angular/material/legacy-list';
import { AppHelper } from '@app/core/app.helper';
import { IListOption } from '@components/selection-list/interfaces/selection-list.interface';
import { SelectionListService } from '@components/selection-list/services/selection-list.service';
import { LANG_DICTIONARY_TOKEN } from '@constants/diConstant';
import { IMainLang } from '@interfaces/languages/main-lang';
import { FuiDestroyService } from '@services/destroy.service';
import { BehaviorSubject, Observable, of, ReplaySubject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-selection-list',
    templateUrl: './selection-list.component.html',
    styleUrls: ['../styles/selection-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [SelectionListService, FuiDestroyService],
})
export class SelectionListComponent<T> implements OnInit {
    constructor(
        @Inject(LANG_DICTIONARY_TOKEN) public readonly dictionary: IMainLang = null,
        public readonly service: SelectionListService,
    ) {}

    /** Список множественного или единичного выбора */
    @Input()
    public set multiple(flag: BooleanInput) {
        this.#multiple = flag === '' || flag === true || flag === '1';
    }

    /** Получить значение multiple */
    public get isMultiple(): boolean {
        return this.#multiple;
    }

    /** Получить значение optionlist */
    public get optionList$() {
        return this.#optionList.asObservable();
    }

    /** Задать значение поиска */
    public set searchStr(str: string) {
        this._search.next(str);
    }

    /** Получить значение поиска */
    public get searchStr(): string {
        return this._search.value;
    }

    /** Сервис отписки */
    readonly #destroy$: FuiDestroyService = inject(FuiDestroyService);

    /** Событие клика по элементу списка */
    @Output() optionClick = new EventEmitter<any>();

    /** Данные списка */
    @Input() list: Observable<T[]> | T[];

    /** Список опций для вывода */
    readonly #optionList: ReplaySubject<IListOption[]> = new ReplaySubject(1);

    /** Данные о родителе */
    @Input() itemData: any;

    /** Событие выбора элементов */
    @Output() selectionEvent: EventEmitter<T> = new EventEmitter();

    /** Список ссылок на элементы */
    @ViewChildren('listOption') private readonly _listOptionRef!: QueryList<MatListOption>;

    /** Список множественного или единичного выбора */
    #multiple: boolean = false;

    /** Значение поиска по списку */
    private _search: BehaviorSubject<string> = new BehaviorSubject<string>('');

    /** Выбранные элементы */
    selectedItems = new SelectionModel<IListOption>(true);

    /** Получить длину списка элементов */
    public getListLength(): number {
        if (Array.isArray(this.list)) {
            return this.list.length;
        } else {
            let length = 0;
            this.list.subscribe((items) => {
                length = items.length;
            });
            return length;
        }
    }

    /** Установка свойства optionList
     * @param list входящий список
     */
    setOptions(list: Observable<T[]> | T[]) {
        let optionsObservable: Observable<T[]>;
        if (Array.isArray(list)) {
            optionsObservable = of(list);
        } else {
            optionsObservable = list;
        }
        optionsObservable.pipe(takeUntil(this.#destroy$)).subscribe((subscriber: T[]) => {
            const arr: IListOption[] = [];
            subscriber.forEach((item: T & IListOption) => {
                arr.push({ id: item.id, name: item.name, ...item });
            });
            this.#optionList.next(arr);
        });
    }

    /**
     * Поиск данных в списке
     * @param srt - строка поиска
     */
    public filterList(srt: string): void {
        if (srt === null || srt?.length === 0) {
            this.setOptions(this.list);
            return;
        }

        // Экранируем строку
        const searchStr = AppHelper.escapingStringForSearch(srt);

        // Регулярное выражение для поиска
        const matchValue = new RegExp(searchStr, 'i');

        const result = [];

        this.list.forEach((item) => {
            if ((item.name + ' ' + item.id).match(matchValue)) {
                result.push(item);
            }
        });

        this.setOptions(result);
    }

    ngOnInit(): void {
        this.setOptions(this.list);
    }
}
